import { createContext, useContext, useState, useEffect } from 'react';
import { 
    fetchAllBookings, 
    fetchUserBookings, 
    createBooking, 
    updateBookingStatus, 
    deleteBooking,
    createCompleteBooking,
    updateBooking
} from '../api/bookingAPI';
/* eslint-disable no-unused-vars */
import {
    fetchAllServices,
    fetchServiceBasePrices,
    fetchServiceAddons
} from '../api/servicesAPI';
import {
    fetchVehicleTypesWithDefaultPrices,
    fetchVehicleTypeWithPrices
} from '../api/vehicleTypesAPI';
import { useAuth } from './AuthContext';
import { useCalendar } from './CalendarContext';
import { checkTimeSlotAvailability as checkTimeSlotAvailabilityAPI } from '../api/calendarAPI';

export const BookingContext = createContext();

export const BookingProvider = ({ children }) => {
    const { currentUser, userRole } = useAuth();
    const { bookTimeSlot, getAvailableTimeSlots, releaseTimeSlot } = useCalendar();
    
    // Vehicle Types State
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [loadingVehicles, setLoadingVehicles] = useState(true);
    const [vehiclesError, setVehiclesError] = useState(null);

    // Services State
    const [services, setServices] = useState([]);
    const [loadingServices, setLoadingServices] = useState(true);
    const [servicesError, setServicesError] = useState(null);

    // Selected Items State
    const [currentStep, setCurrentStep] = useState(0);
    const [selectedCar, setSelectedCar] = useState(null);
    const [selectedService, setSelectedService] = useState(null);
    const [selectedAddOns, setSelectedAddOns] = useState([]);
    const [totalPrice, setTotalPrice] = useState(0);
    
    // Selected Date and Time
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);
    const [location, setLocation] = useState('inStore');

    // Booking Management States
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 10
    });
    const [filters, setFilters] = useState({
        status: null,
        startDate: null,
        endDate: null
    });

    // Fetch vehicle types on mount
    useEffect(() => {
        const loadVehicleTypes = async () => {
            try {
                setLoadingVehicles(true);
                const response = await fetchVehicleTypesWithDefaultPrices();
                if (response.error) throw new Error(response.error.message);
                setVehicleTypes(response.data || []);
            } catch (err) {
                console.error('Error loading vehicle types:', err);
                setVehiclesError(err.message);
            } finally {
                setLoadingVehicles(false);
            }
        };

        loadVehicleTypes();
    }, []);

    // Fetch services on mount
    useEffect(() => {
        const loadServices = async () => {
            try {
                setLoadingServices(true);
                const response = await fetchAllServices();
                if (response.error) throw new Error(response.error.message);
                setServices(response.data || []);
            } catch (err) {
                console.error('Error loading services:', err);
                setServicesError(err.message);
            } finally {
                setLoadingServices(false);
            }
        };

        loadServices();
    }, []);

    // Fetch service prices when a vehicle and service are selected
    useEffect(() => {
        const updatePrices = async () => {
            if (!selectedCar || !selectedService) {
                setTotalPrice(0);
                return;
            }

            try {
                // Get the base price for the selected service and vehicle type
                const basePrice = selectedService.services_base_price?.find(
                    price => price.vehicle_types?.id === selectedCar.id
                )?.price || 0;

                // Calculate total price of selected add-ons
                const addonsTotal = selectedAddOns.reduce((sum, addon) => {
                    // Ensure we're using numeric values
                    const addonPrice = typeof addon.price === 'number' ? addon.price : 0;
                    return sum + addonPrice;
                }, 0);

                // Add $50 fee for at-home service
                const atHomeFee = location === 'atHome' ? 50 : 0;

                // Set the total price (base price + addons + at-home fee)
                const total = basePrice + addonsTotal + atHomeFee;
                setTotalPrice(total);
            } catch (err) {
                console.error('Error updating prices:', err);
                setTotalPrice(0);
            }
        };

        updatePrices();
    }, [selectedCar, selectedService, selectedAddOns, location]);

    // Fetch bookings based on user role
    useEffect(() => {
        if (!currentUser) return;

        const loadBookings = async () => {
            setLoading(true);
            setError(null);

            try {
                const options = {
                    page: pagination.currentPage,
                    limit: pagination.limit,
                    ...filters
                };

                const response = userRole === 'admin'
                    ? await fetchAllBookings(options)
                    : await fetchUserBookings(currentUser.id, options);

                if (response.error) throw new Error(response.error.message);

                // Update bookings
                setBookings(response.data || []);
                
                // Update pagination with proper checks
                setPagination(prev => ({
                    ...prev,
                    currentPage: response.totalCount === 0 ? 1 : response.currentPage,
                    totalCount: response.totalCount || 0,
                    totalPages: Math.max(1, Math.ceil((response.totalCount || 0) / prev.limit))
                }));

                // If current page is greater than total pages, go to last page
                if (pagination.currentPage > Math.ceil((response.totalCount || 0) / pagination.limit)) {
                    changePage(Math.max(1, Math.ceil((response.totalCount || 0) / pagination.limit)));
                }

            } catch (err) {
                setError(err.message);
                console.error('Error loading bookings:', err);
            } finally {
                setLoading(false);
            }
        };

        loadBookings();
    }, [currentUser, userRole, pagination.currentPage, pagination.limit, filters]);

    const selectCar = async (car) => {
        setSelectedCar(car);
        setCurrentStep(1);
    };

    const selectService = async (service) => {
        setSelectedService(service);
        // Fetch addons for the selected service
        const { data } = await fetchServiceAddons(service.id);
        if (data) {
            setSelectedAddOns([]); // Reset addons when changing service
        }
    };

    const clearSelectedCar = () => {
        setSelectedCar(null);
        setSelectedService(null);
        setSelectedAddOns([]);
    };
    
    const selectDateTime = (date, time) => {
        setSelectedDate(date);
        setSelectedTime(time);
        setCurrentStep(3); // Move to next step after date/time selection
    };
    
    const clearDateTime = () => {
        setSelectedDate(null);
        setSelectedTime(null);
    };
    
    // Check if a time slot is available before booking
    const checkTimeSlotAvailability = async (date, time) => {
        if (!date || !time) return false;
        
        try {
            // Use the calendarAPI to check if the time slot is available
            const { success, available } = await checkTimeSlotAvailabilityAPI(date, time);
            
            if (!success) {
                console.error('Failed to check time slot availability');
                return false;
            }
            
            return available;
        } catch (err) {
            console.error('Error checking time slot availability:', err);
            return false;
        }
    };

    // Booking management functions
    const createNewBooking = async (bookingData) => {
        setLoading(true);
        setError(null);

        try {
            // Check if the time slot is still available
            if (bookingData.date && bookingData.time) {
                const isAvailable = await checkTimeSlotAvailability(bookingData.date, bookingData.time);
                
                if (!isAvailable) {
                    throw new Error('This time slot is no longer available. Please select another time.');
                }
            }
            
            const response = await createBooking(bookingData);
            if (response.error) throw new Error(response.error.message);

            // Update calendar availability
            if (bookingData.date && bookingData.time) {
                // Convert date format if needed
                const formattedDate = new Date(bookingData.date).toISOString().split('T')[0];
                await bookTimeSlot(formattedDate, bookingData.time, response.data.id);
            }

            setBookings(prev => [response.data, ...prev]);
            return { success: true, data: response.data };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    };

    // New function to create a complete booking with guest profile handling
    const createCompleteNewBooking = async (bookingDetails) => {
        setLoading(true);
        setError(null);

        try {
            // Check if the time slot is still available
            if (bookingDetails.date && bookingDetails.time) {
                const isAvailable = await checkTimeSlotAvailability(bookingDetails.date, bookingDetails.time);
                
                if (!isAvailable) {
                    throw new Error('This time slot is no longer available. Please select another time.');
                }
            }
            
            const response = await createCompleteBooking(
                bookingDetails, 
                userRole, 
                currentUser?.id
            );
            
            if (!response.success) {
                throw new Error(response.error.message);
            }
            
            // Refresh bookings after successful creation
            if (currentUser) {
                await fetchBookings();
            }
            
            return { 
                success: true, 
                data: response.data 
            };
        } catch (err) {
            setError(err.message);
            return { 
                success: false, 
                error: err.message 
            };
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (bookingId, newStatus) => {
        setLoading(true);
        setError(null);

        try {
            const response = await updateBookingStatus(bookingId, newStatus);
            if (response.error) throw new Error(response.error.message);

            setBookings(prev => 
                prev.map(booking => 
                    booking.id === bookingId 
                        ? { ...booking, status: newStatus }
                        : booking
                )
            );
            return { success: true };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    };

    const removeBooking = async (bookingId) => {
        setLoading(true);
        setError(null);

        try {
            const response = await deleteBooking(bookingId);
            if (!response.success) throw new Error(response.error.message);

            setBookings(prev => prev.filter(booking => booking.id !== bookingId));
            return { success: true };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    };

    const clearBooking = () => {
        setSelectedCar(null);
        setSelectedService(null);
        setSelectedAddOns([]);
        setSelectedDate(null);
        setSelectedTime(null);
        setCurrentStep(0);
        setLocation('inStore');
    };

    const updateFilters = (newFilters) => {
        setFilters(prev => ({ ...prev, ...newFilters }));
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    const changePage = (newPage) => {
        // Ensure newPage is within valid bounds
        const validPage = Math.max(1, Math.min(newPage, pagination.totalPages));
        setPagination(prev => ({
            ...prev,
            currentPage: validPage
        }));
    };

    // Function to manually refresh bookings
    const fetchBookings = async () => {
        setLoading(true);
        setError(null);

        try {
            const options = {
                page: pagination.currentPage,
                limit: pagination.limit,
                ...filters
            };

            const response = userRole === 'admin'
                ? await fetchAllBookings(options)
                : await fetchUserBookings(currentUser.id, options);

            if (response.error) throw new Error(response.error.message);

            setBookings(response.data || []);
            setPagination(prev => ({
                ...prev,
                currentPage: response.totalCount === 0 ? 1 : response.currentPage,
                totalCount: response.totalCount || 0,
                totalPages: Math.max(1, Math.ceil((response.totalCount || 0) / prev.limit))
            }));
            
            return { success: true };
        } catch (err) {
            setError(err.message);
            console.error('Error refreshing bookings:', err);
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    };

    const updateBooking = async (bookingId, updatedData) => {
        setLoading(true);
        setError(null);

        try {
            const response = await updateBooking(bookingId, updatedData);
            if (response.error) throw new Error(response.error.message);

            setBookings(prev => 
                prev.map(booking => 
                    booking.id === bookingId 
                        ? { ...booking, ...updatedData }
                        : booking
                )
            );
            return { success: true };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    };

    const selectLocation = (newLocation) => {
        setLocation(newLocation);
    };

    return (
        <BookingContext.Provider
            value={{
                // Vehicle and service selection
                vehicleTypes,
                loadingVehicles,
                vehiclesError,
                services,
                loadingServices,
                servicesError,
                currentStep,
                setCurrentStep,
                selectedCar,
                setSelectedCar,
                selectedService,
                setSelectedService,
                selectedAddOns,
                setSelectedAddOns,
                totalPrice,
                selectCar,
                selectService,
                clearSelectedCar,
                clearBooking,
                
                // Date and time selection
                selectedDate,
                selectedTime,
                selectDateTime,
                clearDateTime,
                checkTimeSlotAvailability,
                
                // Booking management
                bookings,
                loading,
                error,
                pagination,
                filters,
                createNewBooking,
                createCompleteNewBooking,
                updateStatus,
                removeBooking,
                updateFilters,
                changePage,
                fetchBookings,
                updateBooking,
                
                // Location selection
                location,
                selectLocation
            }}
        >
            {children}
        </BookingContext.Provider>
    );
};

export const useBooking = () => {
    const context = useContext(BookingContext);
    if (!context) {
        throw new Error('useBooking must be used within a BookingProvider');
    }
    return context;
}; 